import { IElementType, isIElementCamView, isIElementGenericAnnotation, isIElementPointCloudStream, isIElementSection } from "@faro-lotv/ielement-types";
import { addIElements, createSignedUrlForIElementUri, createSignedUrlsForIElementSources, selectAncestor, selectDatasetOrArea, selectIElement, selectIElementWorldPosition, selectIElementWorldTransform } from "@faro-lotv/project-source";
import { selectMarkupMeasurement } from "@pages/project-details/project-markups/sidepanel/preview/select-markup-measurement";

/** Position, rotation and scale in the world */

/** Everything needed to render the point cloud around an annotation */

/** Most point clouds whose ancestors are fetched to pick the one closest to the annotation */
const MAX_CANDIDATES = 50;

/** The point cloud an annotation was placed in, found by `findMarkupPointCloud` */

/**
 * Finds the scan an annotation was placed in, among the point clouds of its data session, or its
 * area if it is in none: the one closest to the annotation. The merged point cloud of the section
 * the annotation is stored in, the overview map, is only used without any scan. Needs the
 * annotation and its ancestors to be in the store already.
 *
 * @returns undefined if there is no point cloud stream near the annotation
 */
export function findMarkupPointCloud({
  markupId,
  projectApiClient,
  signal
}) {
  return async (dispatch, getState) => {
    const element = selectAnnotationOrMarkup(markupId)(getState());
    const reference = element && selectDatasetOrArea(element)(getState());
    if (!reference) {
      return;
    }
    const pointClouds = (await projectApiClient.getAllIElements({
      ancestorIds: [reference.id],
      types: [IElementType.pointCloudStream],
      signal
    })).filter(isIElementPointCloudStream).slice(0, MAX_CANDIDATES);
    if (!pointClouds.length) {
      return;
    }

    // Their ancestors are needed for their world positions
    const [ancestors, camViews] = await Promise.all([projectApiClient.getAllIElements({
      descendantIds: pointClouds.map(({
        id
      }) => id),
      signal
    }), projectApiClient.getAllIElements({
      parentIds: [element.id],
      types: [IElementType.camView],
      signal
    })]);
    dispatch(addIElements([...ancestors, ...pointClouds, ...camViews]));
    const state = getState();
    const section = selectAncestor(element, isIElementSection)(state);
    const scans = pointClouds.filter(({
      parentId
    }) => !section || parentId !== section.id);
    const candidates = scans.length ? scans : pointClouds;
    const pointCloud = selectClosest(candidates, markupId)(state);
    return pointCloud && {
      pointCloud,
      camView: camViews.find(isIElementCamView)
    };
  };
}

/** Signs the files of the point cloud found by `findMarkupPointCloud` and computes where to look from */
export function loadMarkupPointCloud({
  markupId,
  pointCloud,
  camView,
  projectApiClient,
  signal
}) {
  return async (_dispatch, getState) => {
    const files = await fetchPotreeFiles(projectApiClient, pointCloud, signal);
    if (!files) {
      return;
    }
    const [metadata, hierarchy, octree] = createSignedUrlsForIElementSources(projectApiClient, pointCloud.id, {
      sources: [files.metaDataUrl, files.hierarchyUrl, files.octreeURL]
    });
    const state = getState();
    return {
      id: pointCloud.id,
      potreeUrls: {
        metadata,
        hierarchy,
        octree
      },
      pointCloudTransform: selectIElementWorldTransform(pointCloud.id)(state),
      markupPosition: selectIElementWorldPosition(markupId)(state),
      cameraTransform: camView ? selectIElementWorldTransform(camView.id)(state) : undefined,
      measurement: selectMarkupMeasurement(markupId)(state)
    };
  };
}

/** Index file of a Potree point cloud stream, which its `uri` points to */

/** @returns the URLs of the files of the point cloud, or undefined if it is not a Potree point cloud */
async function fetchPotreeFiles(projectApiClient, pointCloud, signal) {
  const url = await createSignedUrlForIElementUri(projectApiClient, pointCloud).requestSignedUrl();
  const response = await fetch(url, {
    signal
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch the point cloud index: ${response.status}`);
  }
  const files = await response.json();
  return isPotreeFiles(files) ? files : undefined;
}
function isPotreeFiles(files) {
  return typeof files === "object" && files !== null && "metaDataUrl" in files && typeof files.metaDataUrl === "string" && "hierarchyUrl" in files && typeof files.hierarchyUrl === "string" && "octreeURL" in files && typeof files.octreeURL === "string";
}

/** @returns the annotation the markup belongs to, or the markup itself if it has none */
function selectAnnotationOrMarkup(markupId) {
  return state => {
    const markup = selectIElement(markupId)(state);
    return selectAncestor(markup, isIElementGenericAnnotation)(state) ?? markup;
  };
}

/** @returns the point cloud whose origin is the closest to the element, e.g. the annotation */
function selectClosest(pointClouds, elementId) {
  return state => {
    const [x, y, z] = selectIElementWorldPosition(elementId)(state);
    let closest;
    let closestDistance = Infinity;
    for (const pointCloud of pointClouds) {
      const [px, py, pz] = selectIElementWorldPosition(pointCloud.id)(state);
      const distance = (px - x) ** 2 + (py - y) ** 2 + (pz - z) ** 2;
      if (distance < closestDistance) {
        closest = pointCloud;
        closestDistance = distance;
      }
    }
    return closest;
  };
}